/**
 * useSsiLockGuard — route guard for the Semi-Structured Interview pages.
 *
 * The Patient Overview locks the SSI card in the one-clinician SLaM / 3
 * Borough workflow until the Lead chooses "Proceed" at the end of the
 * Clinical Interview, and while the assessment is ended. A locked card is not
 * enough on its own: the SSI pages are reachable by URL (bookmarks, browser
 * history, the "Continue" link on the dashboard), so each page applies the
 * same predicate and sends the user back to the overview with a short
 * explanation when the module is locked.
 *
 * The predicate is the shared shouldLockAdhdSemiStructuredInterview from
 * moduleUnlockUtils, so the card and the pages can never disagree. For every
 * other workflow the predicate is false and the hook does nothing.
 *
 * @param {Object} args
 * @param {Object|null} args.patientDataModel
 * @param {Object} args.wf  useWorkflowConfig result
 * @param {string} args.patientId
 * @returns {boolean} true when the redirect has been issued (callers may use
 *   it to avoid rendering the form for one frame).
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  shouldLockAdhdSemiStructuredInterview,
  getAdhdSemiStructuredInterviewLockedTooltip,
} from '../utils/moduleUnlockUtils';

export default function useSsiLockGuard({ patientDataModel, wf, patientId }) {
  const navigate = useNavigate();
  const isLocked = Boolean(
    patientDataModel && shouldLockAdhdSemiStructuredInterview({ patientDataModel, wf }),
  );
  const message = isLocked
    ? getAdhdSemiStructuredInterviewLockedTooltip({ patientDataModel, wf })
    : '';

  useEffect(() => {
    if (!isLocked) return;
    toast.info(message || 'The Semi-Structured Interview is locked for this patient.');
    navigate(`/patient/${patientId}`, { replace: true });
  }, [isLocked, message, navigate, patientId]);

  return isLocked;
}
